import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { bookingTaxDocumentsPath } from '@core/api/queryPaths';
import type { TaxDocument } from '@core/api/taxDocuments';
import type { TaxDocumentKind, TaxDocumentStatus } from './generated/enums';
import { apiFetch } from './client';
import type { Paged } from './masters';

export const taxDocumentsKey = ['tax-document'] as const;

export type * from '@core/api/taxDocuments';

/**
 * `| undefined` is spelled out on every field rather than left to the `?`, because
 * `exactOptionalPropertyTypes` is on and a screen holding filter state in `useState` produces
 * "present but undefined" rather than "absent".
 */
export interface TaxDocumentFilters {
  kind?: TaxDocumentKind | undefined;
  status?: TaxDocumentStatus | undefined;
  from?: string | undefined;
  to?: string | undefined;
  search?: string | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

export function useTaxDocuments(branchId: number | undefined, filters: TaxDocumentFilters = {}) {
  const { kind, status, from, to, search, page = 1, pageSize = 25 } = filters;

  return useQuery({
    // The acting branch is part of the key for the reason the catalogue's is: without it,
    // switching branches keeps serving the previous shop's register.
    queryKey: [...taxDocumentsKey, { branchId, kind, status, from, to, search, page, pageSize }],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

      if (kind) {
        params.set('kind', kind);
      }

      if (status) {
        params.set('status', status);
      }

      if (from) {
        params.set('from', from);
      }

      if (to) {
        params.set('to', to);
      }

      if (search) {
        params.set('search', search);
      }

      return apiFetch<Paged<TaxDocument>>(`/api/tax-document?${params.toString()}`);
    },
    enabled: branchId !== undefined,
    placeholderData: (previous) => previous,
  });
}

/**
 * What the filter charged in total, over every matching row rather than the page on screen.
 *
 * Signed — a credit note counts against the rest — and cancelled documents are out of it entirely,
 * because they charged nothing. Their numbers stay in the sequence regardless.
 */
export interface TaxRegisterSummary {
  invoices: number;
  creditNotes: number;
  debitNotes: number;
  taxableValue: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  documentTotal: number;
}

/**
 * The period's totals.
 *
 * <p>
 * <strong>`enabled` is the caller's, and it is a permission rather than a preference.</strong> The
 * endpoint is guarded by `SalesInvoice:ViewFinancials` — the register is a list of documents a
 * counter clerk may well need, while this is the shop's turnover on one screen — so a screen that
 * asked for it unconditionally would put a 403 in front of every counter user who opened it.
 * </p>
 */
export function useTaxRegisterSummary(
  branchId: number | undefined,
  filters: TaxDocumentFilters,
  enabled: boolean,
) {
  const { kind, status, from, to, search } = filters;

  return useQuery({
    queryKey: [...taxDocumentsKey, 'summary', { branchId, kind, status, from, to, search }],
    queryFn: () => {
      const params = new URLSearchParams();

      if (kind) {
        params.set('kind', kind);
      }

      if (status) {
        params.set('status', status);
      }

      if (from) {
        params.set('from', from);
      }

      if (to) {
        params.set('to', to);
      }

      if (search) {
        params.set('search', search);
      }

      return apiFetch<TaxRegisterSummary>(`/api/tax-document/summary?${params.toString()}`);
    },
    enabled: enabled && branchId !== undefined,
    placeholderData: (previous) => previous,
  });
}

/** One design that cannot be invoiced, named the way its own screen names it. */
export interface UnratedDesign {
  itemId: number;
  code: string;
  name: string;
  categoryName: string;
}

/**
 * Whether this branch could invoice right now.
 *
 * <p>
 * Every field is a setting whose absence stops the shop trading. An invoice is raised inside the
 * issue transaction, so a missing one does not merely lose the paperwork — the whole handover rolls
 * back, at the counter, with the customer holding the garments.
 * </p>
 */
export interface InvoiceReadiness {
  isRegistered: boolean;
  hasPlaceOfSupply: boolean;
  hasInvoiceSeries: boolean;
  hasNoteSeries: boolean;
  designsWithoutTaxRate: number;
  examples: UnratedDesign[];
}

export function useInvoiceReadiness(branchId: number | undefined) {
  return useQuery({
    queryKey: [...taxDocumentsKey, 'readiness', branchId],
    queryFn: () => apiFetch<InvoiceReadiness>('/api/tax-document/readiness'),
    enabled: branchId !== undefined,

    // Settings, not traffic. Re-asking on every visit to the register is waste, and the answer only
    // changes when somebody edits a design, a branch or a numbering series.
    staleTime: 5 * 60 * 1000,
  });
}

/** Everything raised against one booking — what the booking screen lists beside its payments. */
export function useBookingTaxDocuments(bookingId: number | undefined) {
  return useQuery({
    queryKey: [...taxDocumentsKey, 'booking', bookingId],
    queryFn: () => apiFetch<TaxDocument[]>(bookingTaxDocumentsPath(bookingId ?? 0)),
    enabled: bookingId !== undefined,
  });
}

/**
 * Withdraws a document raised in error.
 *
 * The only mutation a tax document has: there is no create and no update, because a statutory
 * document comes out of the supply that made it and is never edited afterwards.
 */
export function useCancelTaxDocument() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      apiFetch<TaxDocument>(`/api/tax-document/${id}/cancel`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: taxDocumentsKey }),
  });
}
